/**
 * Customer management API service
 */
import apiClient from './api';
import {
  Customer,
  CustomerGroup,
  CustomerCreateData,
  CustomerUpdateData,
  CustomerGroupCreateData,
  CustomerGroupUpdateData,
  CustomerListResponse,
  CustomerGroupListResponse,
  CustomerTerms,
  CustomerFilters,
  PricingTier,
  PricingTierCreateData,
  PricingTierUpdateData
} from '../types/customer';

export class CustomerService {
  // Customer Group Management
  static async getCustomerGroups(params?: {
    page?: number;
    per_page?: number;
    is_active?: boolean;
    search?: string;
  }): Promise<CustomerGroupListResponse> {
    const response = await apiClient.get('/admin/customers/groups', { params });
    return response.data;
  }

  static async getCustomerGroup(id: string): Promise<CustomerGroup> {
    const response = await apiClient.get(`/admin/customers/groups/${id}`);
    return response.data;
  }

  static async createCustomerGroup(data: CustomerGroupCreateData): Promise<CustomerGroup> {
    const response = await apiClient.post('/admin/customers/groups', data);
    return response.data;
  }

  static async updateCustomerGroup(id: string, data: CustomerGroupUpdateData): Promise<CustomerGroup> {
    const response = await apiClient.put(`/admin/customers/groups/${id}`, data);
    return response.data;
  }

  static async deleteCustomerGroup(id: string): Promise<void> {
    await apiClient.delete(`/admin/customers/groups/${id}`);
  }

  static async applyGroupRules(groupId: string): Promise<{ message: string; result: any }> {
    const response = await apiClient.post(`/admin/customers/groups/${groupId}/apply-rules`);
    return response.data;
  }

  // Customer Management
  static async getCustomers(params?: {
    page?: number;
    per_page?: number;
    customer_type?: string;
    customer_group_id?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<CustomerListResponse> {
    const response = await apiClient.get('/admin/customers', { params });
    return response.data;
  }

  static async getCustomer(id: string): Promise<Customer> {
    const response = await apiClient.get(`/admin/customers/${id}`);
    return response.data;
  }

  static async createCustomer(data: CustomerCreateData): Promise<Customer> {
    const response = await apiClient.post('/admin/customers', data);
    return response.data;
  }

  static async updateCustomer(id: string, data: CustomerUpdateData): Promise<Customer> {
    const response = await apiClient.put(`/admin/customers/${id}`, data);
    return response.data;
  }

  static async deleteCustomer(id: string): Promise<void> {
    await apiClient.delete(`/admin/customers/${id}`);
  }

  static async updateCustomerCreditLimit(id: string, creditLimit: number): Promise<Customer> {
    const response = await apiClient.patch(`/admin/customers/${id}/credit-limit`, {
      credit_limit: creditLimit
    });
    return response.data;
  }

  static async assignCustomerToGroup(id: string, groupId: string): Promise<Customer> {
    const response = await apiClient.patch(`/admin/customers/${id}/group`, {
      customer_group_id: groupId
    });
    return response.data;
  }

  static async getCustomerTerms(id: string): Promise<CustomerTerms> {
    const response = await apiClient.get(`/admin/customers/${id}/terms`);
    return response.data;
  }

  static async assignCataloguesToCustomer(id: string, catalogueIds: string[]): Promise<Customer> {
    const response = await apiClient.patch(`/admin/customers/${id}/catalogues`, {
      catalogue_ids: catalogueIds
    });
    return response.data;
  }

  static async advancedCustomerSearch(filters: CustomerFilters & {
    page?: number;
    per_page?: number;
  }): Promise<CustomerListResponse> {
    const response = await apiClient.get('/admin/customers/search/advanced', { params: filters });
    return response.data;
  }

  // Pricing Tier Management (assuming these endpoints exist based on task requirements)
  static async getPricingTiers(params?: {
    page?: number;
    per_page?: number;
    is_active?: boolean;
    search?: string;
  }): Promise<{ items: PricingTier[]; total: number; page: number; per_page: number; pages: number }> {
    const response = await apiClient.get('/admin/pricing/tiers', { params });
    return response.data;
  }

  static async getPricingTier(id: string): Promise<PricingTier> {
    const response = await apiClient.get(`/admin/pricing/tiers/${id}`);
    return response.data;
  }

  static async createPricingTier(data: PricingTierCreateData): Promise<PricingTier> {
    const response = await apiClient.post('/admin/pricing/tiers', data);
    return response.data;
  }

  static async updatePricingTier(id: string, data: PricingTierUpdateData): Promise<PricingTier> {
    const response = await apiClient.put(`/admin/pricing/tiers/${id}`, data);
    return response.data;
  }

  static async deletePricingTier(id: string): Promise<void> {
    await apiClient.delete(`/admin/pricing/tiers/${id}`);
  }

  static async bulkUpdatePricing(data: {
    tier_id?: string;
    customer_ids?: string[];
    price_adjustments: Record<string, number>;
  }): Promise<{ message: string; updated_count: number }> {
    const response = await apiClient.post('/admin/pricing/bulk-update', data);
    return response.data;
  }
}